(() => {
  class ShReviews extends HTMLElement {
    connectedCallback() {
      this.build = this.build.bind(this);
      this.sync = this.sync.bind(this);

      if (!this.setup()) {
        this.watcher = new MutationObserver(() => {
          if (this.setup()) this.watcher.disconnect();
        });
        this.watcher.observe(this, { childList: true, subtree: true });
      }
    }

    setup() {
      if (this.querySelector('.kl_reviews__carousel__link')) {
        this.querySelector('.sh-reviews__skeleton')?.remove();
      }

      return this.build();
    }

    disconnectedCallback() {
      this.watcher?.disconnect();
      this.railWatcher?.disconnect();
      this.removeEventListener('scroll', this.sync, { capture: true });
      this.wrapper?.removeEventListener('transitionend', this.sync);
    }

    build() {
      const carousel = this.querySelector('.kl_reviews__carousel');
      const swiperEl = this.querySelector('.swiper');
      const wrapper = swiperEl?.querySelector('.swiper-wrapper');
      if (!carousel || !swiperEl || !wrapper) return false;

      const slides = this.slides(wrapper);
      if (slides.length < 2) return false;

      this.swiperEl = swiperEl;
      this.wrapper = wrapper;

      this.dotList?.remove();
      this.dotList = document.createElement('div');
      this.dotList.className = 'sh-reviews__dots';

      this.dots = slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sh-reviews__dot';
        dot.setAttribute('aria-label', `${index + 1}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotList.appendChild(dot);
        return dot;
      });

      carousel.appendChild(this.dotList);

      this.addEventListener('scroll', this.sync, { capture: true, passive: true });
      this.railWatcher = new MutationObserver(this.sync);
      this.railWatcher.observe(wrapper, { attributes: true, attributeFilter: ['style'] });
      wrapper.addEventListener('transitionend', this.sync);

      this.sync();
      return true;
    }

    /* Loop mode clones slides — the clones must not get their own dot. */
    slides(wrapper) {
      return Array.from(wrapper.querySelectorAll('.swiper-slide')).filter(
        (slide) => !slide.classList.contains('swiper-slide-duplicate')
      );
    }

    progress() {
      const slides = this.slides(this.wrapper);
      if (slides.length < 2) return 0;

      const viewport = this.swiperEl.getBoundingClientRect();
      const first = slides[0].getBoundingClientRect();
      const last = slides[slides.length - 1].getBoundingClientRect();

      const max = last.right - first.left - viewport.width;
      if (max <= 1) return 0;

      return Math.min(Math.max((viewport.left - first.left) / max, 0), 1);
    }

    /* Whichever of the two actually scrolls, if either does. */
    scroller() {
      return [this.wrapper, this.swiperEl].find((el) => {
        const overflow = getComputedStyle(el).overflowX;
        return (overflow === 'auto' || overflow === 'scroll') && el.scrollWidth - el.clientWidth > 1;
      });
    }

    goTo(index) {
      const swiper = this.swiperEl.swiper;
      if (swiper?.slideToLoop) return swiper.slideToLoop(index);
      if (swiper?.slideTo) return swiper.slideTo(index);

      if (this.scroller()) {
        const slide = this.slides(this.wrapper)[index];
        if (slide) {
          slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          return;
        }
      }

      const delta = index - this.active;
      const button = this.querySelector(
        delta > 0 ? '.kl_reviews__carousel__next_button' : '.kl_reviews__carousel__prev_button'
      );
      for (let step = 0; step < Math.abs(delta); step += 1) button?.click();
    }

    sync() {
      if (!this.dots?.length) return;

      const steps = this.dots.length - 1;
      this.active = steps > 0 ? Math.round(this.progress() * steps) : 0;

      this.dots.forEach((dot, index) =>
        dot.setAttribute('aria-current', index === this.active ? 'true' : 'false')
      );
    }
  }

  if (!customElements.get('sh-reviews')) customElements.define('sh-reviews', ShReviews);
})();
